import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/common";
import { WS_STATUS } from "@/lib/track";
import { fmtDate } from "@/lib/helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import * as Icons from "lucide-react";

function WsStatus({ status }) {
  const m = WS_STATUS[status] || WS_STATUS.planifie;
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${m.cls}`} data-testid={`ws-status-${status}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />{m.label}</span>;
}

function NewWorksite({ open, onOpenChange, onCreated }) {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [f, setF] = useState({ title: "", client_id: "", site_id: "", client_contact: "", planned_start: "", planned_end: "", notify_client: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/clients").then((r) => setClients(r.data)); }, [open]);
  useEffect(() => { if (f.client_id) api.get("/sites", { params: { client_id: f.client_id } }).then((r) => setSites(r.data)); else setSites([]); }, [f.client_id]);
  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/track/worksites", { ...f, prestations: [], intervenant_ids: [] });
      toast.success(`Chantier ${data.reference} créé${!f.notify_client ? "" : data.emailed ? ` — e-mail envoyé au client (${data.emailed})` : " — aucun e-mail envoyé (pas d'adresse client ou envoi indisponible)"}`);
      onCreated(data.id);
    }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ws-new-dialog">
        <DialogHeader><DialogTitle>Nouveau chantier</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Intitulé du chantier *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ws-title" /></div>
          <div className="space-y-1.5"><Label>Client *</Label>
            <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v, site_id: "" })}>
              <SelectTrigger data-testid="ws-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Site *</Label>
            <Select value={f.site_id} onValueChange={(v) => setF({ ...f, site_id: v })} disabled={!f.client_id}>
              <SelectTrigger data-testid="ws-site"><SelectValue placeholder={f.client_id ? "Choisir un site" : "Sélectionnez un client"} /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Début prévu</Label><Input type="date" value={f.planned_start} onChange={(e) => setF({ ...f, planned_start: e.target.value })} data-testid="ws-planned-start" /></div>
            <div className="space-y-1.5"><Label>Fin prévue</Label><Input type="date" value={f.planned_end} onChange={(e) => setF({ ...f, planned_end: e.target.value })} data-testid="ws-planned-end" /></div>
          </div>
          <label className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            <Switch checked={f.notify_client} onCheckedChange={(v) => setF({ ...f, notify_client: v })} data-testid="ws-notify-client" />
            <span>Envoyer un e-mail au client pour l'informer et lui donner l'accès à son espace</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={create} disabled={!f.title.trim() || !f.client_id || !f.site_id || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ws-create">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Créer le chantier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TrackList() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("track.manage");
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({ status: "", search: "", archived: false });
  const [searchParams, setSearchParams] = useSearchParams();
  const [openNew, setOpenNew] = useState(false);
  useEffect(() => { if (searchParams.get("new") === "1" && canManage) { setOpenNew(true); setSearchParams({}, { replace: true }); } }, [searchParams, canManage, setSearchParams]);

  const load = useCallback(async () => {
    setError(false); setItems(null);
    const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
    try { const { data } = await api.get("/track/worksites", { params: clean }); setItems(data); }
    catch { setError(true); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const restore = async (e, wid) => { e.stopPropagation(); try { await api.post(`/track/worksites/${wid}/restore`); toast.success("Chantier restauré"); load(); } catch (err) { toast.error(apiError(err)); } };
  const remove = async (e, wid) => { e.stopPropagation(); try { await api.delete(`/track/worksites/${wid}`); toast.success("Chantier supprimé"); load(); } catch (err) { toast.error(apiError(err)); } };

  return (
    <div className="space-y-5" data-testid="track-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0D2E24] lg:text-3xl">SIGNA TRACK</h1>
          <p className="text-sm text-slate-500">Suivez l'avancement de nos interventions.</p>
        </div>
        {canManage && <Button onClick={() => setOpenNew(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="track-add-btn">
          <Icons.Plus className="mr-1.5 h-4 w-4" /> Nouveau chantier
        </Button>}
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <div className="relative col-span-2 lg:col-span-2">
            <Icons.Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} placeholder="Référence, intitulé…" className="pl-8" data-testid="track-search" />
          </div>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, status: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="track-filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{Object.entries(WS_STATUS).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant={filters.archived ? "default" : "outline"} onClick={() => setFilters((p) => ({ ...p, archived: !p.archived }))}
            className={filters.archived ? "bg-slate-700 hover:bg-slate-800" : ""} data-testid="track-filter-archived">
            <Icons.Archive className="mr-1.5 h-4 w-4" />{filters.archived ? "Archivés" : "Actifs"}
          </Button>
        </div>
      </Card>

      {error ? (
        <Card className="p-6 text-center text-sm text-rose-600" data-testid="track-error">Impossible de charger les chantiers. Réessayez plus tard.</Card>
      ) : !items ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Card key={i} className="h-20 animate-pulse bg-slate-100" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon="HardHat" title="Aucun chantier" testId="track-empty">Créez votre premier chantier pour suivre son avancement.</EmptyState>
      ) : (
        <div className="space-y-2">
          {items.map((w) => (
            <Card key={w.id} onClick={() => navigate(`/track/${w.id}`)} className="cursor-pointer p-4 transition-shadow hover:shadow-md" data-testid={`ws-row-${w.reference}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"><Icons.HardHat className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800"><span className="font-mono-data text-xs text-slate-400">{w.reference}</span> · {w.title}</p>
                  <p className="truncate text-sm text-slate-500">{w.client_name} · {w.site_name} · {w.steps_done}/{w.steps_total} étapes</p>
                </div>
                {w.open_requests > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700" data-testid={`ws-requests-${w.reference}`} title="Demandes client à traiter">
                    <Icons.MessageSquareDot className="h-3.5 w-3.5" />{w.open_requests}
                  </span>
                )}
                <WsStatus status={w.status} />
                {filters.archived && canManage ? (
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="text-emerald-700" onClick={(e) => restore(e, w.id)} data-testid={`ws-restore-${w.reference}`}><Icons.ArchiveRestore className="mr-1 h-3.5 w-3.5" />Restaurer</Button>
                    <Button size="sm" variant="outline" className="text-rose-600" onClick={(e) => remove(e, w.id)} data-testid={`ws-delete-${w.reference}`}><Icons.Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ) : (
                  <Icons.ChevronRight className="hidden h-4 w-4 text-slate-300 sm:block" />
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <NewWorksite open={openNew} onOpenChange={setOpenNew} onCreated={(id) => { setOpenNew(false); navigate(`/track/${id}`); }} />
    </div>
  );
}
